import React from 'react';
import { includes } from 'lodash';
import PropTypes from 'prop-types';

import LayerAccessTokenField from '../../atoms/LayerAccessTokenField';
import LayerAddressIndexField from '../../atoms/LayerAddressIndexField';
import LayerAttributionField from '../../atoms/LayerAttributionField';
import LayerCSVSeparatorField from '../../atoms/LayerCSVSeparatorField';
import LayerExcelFields from '../../atoms/LayerExcelFields';
import LayerFeatureSelector from '../../atoms/LayerFeatureSelector';
import LayerFileTypeField from '../../atoms/LayerFileTypeField';
import LayerFiwareFields from '../../atoms/LayerFiwareFields';
import LayerGeoImageFields from '../../atoms/LayerGeoImageFields';
import LayerGeorefTypeField from '../../atoms/LayerGeorefTypeField';
import LayerGeoTiffResolutionField from '../../atoms/LayerGeoTiffResolutionField';
import LayerH3IndexField from '../../atoms/LayerH3IndexField';
import LayerImageURLField from '../../atoms/LayerImageURLField';
import LayerIndoorLevelField from '../../atoms/LayerIndoorLevelField';
import LayerIntervalField from '../../atoms/LayerIntervalField';
import LayerJoinFields from '../../atoms/LayerJoinFields';
import LayerLocationIndexField from '../../atoms/LayerLocationIndexField';
import LayerMapBoxFields from '../../atoms/LayerMapBoxFields';
import LayerOGRFields from '../../atoms/LayerOGRFields';
import LayerPostgreSQLFields from '../../atoms/LayerPostgreSQLFields';
import LayerSubdomainField from '../../atoms/LayerSubdomainField';
import LayerTableSpatialField from '../../atoms/LayerTableSpatialField';
import LayerUploadFileField from '../../atoms/LayerUploadFileField';
import LayerURLField from '../../atoms/LayerURLField';
import LayerWMSFields from '../../atoms/LayerWMSFields';
import LayerWMTSFormat from '../../atoms/LayerWMTSFormat';
import LayerWMTSLayerField from '../../atoms/LayerWMTSLayerField';
import LayerWMTSStyleField from '../../atoms/LayerWMTSStyleField';
import LayerWMTSTileMatrixSetField from '../../atoms/LayerWMTSTileMatrixSetField';
import LayerZoomField from '../../atoms/LayerZoomField';
import LayerAnnotationsFields from '../../atoms/LayerAnnotationsFields';
import LayerAnnotationsRealTimeFields from '../../atoms/LayerAnnotationsRealTimeFields';

import { LAYER_SUBTYPES, LAYER_TYPES } from '../../../api/Layers/Layers';

import BlankState from '../BlankState';
import LayerOGCCollectionSelector from '../../atoms/LayerOGCCollectionSelector';

const isFeatureReady = (layerType, layerSubType) => {
  if (LAYER_TYPES.THEMATIC === layerType) {
    if (
      includes(
        [LAYER_SUBTYPES.UPLOADED_FILE, LAYER_SUBTYPES.FIWARE, LAYER_SUBTYPES.EXTERNAL_SERVER],
        layerSubType
      )
    ) {
      return false;
    }
  }
  if (LAYER_TYPES.SPATIAL === layerType) {
    if (
      includes(
        [LAYER_SUBTYPES.EXTERNAL_SERVER, LAYER_SUBTYPES.FIWARE, LAYER_SUBTYPES.UPLOADED_FILE],
        layerSubType
      )
    ) {
      return false;
    }
  }
  return true;
};

const comingSoonMessage = (layerType, layerSubType) => {
  if (LAYER_TYPES.JOIN === layerType) {
    return 'Here you will be able to select existing layers to join';
  }
  if (LAYER_TYPES.THEMATIC === layerType) {
    return 'Here you will be able to add thematic layers';
  }
  if (LAYER_TYPES.SPATIAL === layerType) {
    switch (layerSubType) {
      case LAYER_SUBTYPES.EXTERNAL_SERVER:
        return 'Here you will be able to set up a connection to an external server';
      case LAYER_SUBTYPES.UPLOADED_FILE:
        return 'Here you will be able to upload a layer file';
      default:
        return 'This feature will be here soon';
    }
  }
  return '';
};

const LayerFields = props => {
  const { layerType, layerSubType } = props;
  const featureNotReady = !isFeatureReady(layerType, layerSubType);
  if (featureNotReady) {
    return (
      <BlankState
        icon={{ style: 'solid', symbol: 'cogs' }}
        title="This feature is not ready yet. Coming Soon"
        subtitle={comingSoonMessage(layerType, layerSubType)}
      />
    );
  }
  return (
    <>
      <LayerURLField {...props} />
      <LayerOGCCollectionSelector {...props} />
      <LayerFeatureSelector {...props} />
      <LayerOGRFields {...props} />
      <LayerMapBoxFields {...props} />
      <LayerIntervalField {...props} />
      <LayerSubdomainField {...props} />
      <LayerAnnotationsFields {...props} />
      <LayerAnnotationsRealTimeFields {...props} />
      <LayerPostgreSQLFields {...props} />
      <LayerWMSFields {...props} />
      <LayerWMTSLayerField {...props} />
      <LayerWMTSTileMatrixSetField {...props} />
      <LayerWMTSStyleField {...props} />
      <LayerFiwareFields {...props} />
      <LayerFileTypeField {...props} />
      <LayerUploadFileField {...props} />
      <LayerJoinFields {...props} />
      <LayerWMTSFormat {...props} />
      <LayerCSVSeparatorField {...props} />
      <LayerExcelFields {...props} />
      <LayerGeorefTypeField {...props} />
      <LayerTableSpatialField {...props} />
      <LayerH3IndexField {...props} />
      <LayerLocationIndexField {...props} />
      <LayerAddressIndexField {...props} />
      <LayerAccessTokenField {...props} />
      <LayerGeoImageFields {...props} />
      <LayerGeoTiffResolutionField {...props} />
      <LayerIndoorLevelField {...props} />
      <LayerZoomField {...props} />
      <LayerImageURLField {...props} />
      <LayerAttributionField {...props} />
    </>
  );
};
LayerFields.propTypes = {
  layerType: PropTypes.string.isRequired,
  layerSubType: PropTypes.string.isRequired
};

export default LayerFields;
